import {
  IconPhotoFilled,
  IconStarFilled,
  IconMapPin,
  IconCalendar,
  IconUsers,
  IconBedFilled,
  IconWifi,
  IconToolsKitchen2,
  IconBeach,
  IconSnowflake,
  IconComet,
} from "@tabler/icons-react";
import { motion } from "framer-motion";
import { useRef, useState } from "react";
import { useNavigate } from "react-router";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { IconHeart, IconHeartFilled } from "@tabler/icons-react";
import { FocusContainer, OverlayLink } from "src/components/UI/OverlayLink/OverlayLink";
import PaddedView from "src/components/UI/PaddedView/PaddedView";
import Button from "src/components/UI/Button/Button";
import { useDeviceContext } from "src/context/device-context";
import { useLocaleContext } from "src/context/locale-context";
import { favoritesActions, selectIsFavorited } from "src/store/favorites";
import classes from "./CheapHotelDeals.module.css";
import { useImageIntersectionObserver } from "src/hooks/useImageIntersectionObserver";
import NoDataSVG from "src/components/UI/SVG/NoDataSVG";
import { doMoneyMultiplication } from "src/utils/money-utils";
import { getAmenityLabel } from "src/utils/amenity-labels";
import { IMG_SIZES } from "src/pages/loaders/UserTripsLoader";

export function CheapHotelDeal({ item }) {
  const [imgErr, setImgErr] = useState({ original: false, default: false });
  const { getStringRes, currencySymbol, currentLanguage } = useLocaleContext();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const favoriteId = `hotel-${item.id}`;
  const isFavorite = useSelector((state) => selectIsFavorited(state, favoriteId));

  const onToggleFavorite = (e) => {
    e.preventDefault();
    e.stopPropagation();
    dispatch(favoritesActions.toggleFavorite({ id: favoriteId, type: "hotel", data: item }));
    if (!isFavorite) toast.success(getStringRes("favorites.toast.saved"));
  };

  const onErrorHandler = (type) => setImgErr((prev) => ({ ...prev, [type]: true }));

  const getAmenityIcon = (amenity) => {
    switch (amenity.toLowerCase()) {
      case "free wifi":
      case "wifi":
        return <IconWifi size={14} />;
      case "breakfast":
      case "restaurant":
        return <IconToolsKitchen2 size={14} />;
      case "pool":
      case "beachfront":
      case "spa":
        return <IconBeach size={14} />;
      case "gym":
      case "fitness":
        return <IconSnowflake size={14} />;
      default:
        return <IconBedFilled size={14} />;
    }
  };

  const guestCount = Array.isArray(item.guests)
    ? item.guests.reduce((sum, count) => sum + Number(count || 0), 0)
    : parseInt(item.guests) || 1;
  const rating = Number(item.rating || 0);

  // Calculate price per person
  const pricePerPerson =
    item.totalPrice && guestCount
      ? doMoneyMultiplication(parseFloat(item.totalPrice), 1 / guestCount)
      : null;

  const handleViewDeal = () => {
    const link = item.link.startsWith("/") ? item.link : `/${item.link}`;
    navigate(link);
  };

  return (
    <FocusContainer tag="div" className={classes.hotelItem}>
      <OverlayLink
        className={classes.cardLink}
        data-focus-trigger
        to={item.link.startsWith("/") ? item.link : `/${item.link}`}>
        {/* Hotel Image with Rooms Count Badge */}
        <div className={classes.hotelImageContainer}>
          {imgErr.default ? (
            <div className={classes.imgError}>
              <div className={classes.frame}>
                <IconPhotoFilled size="100%" color="var(--ui-4)" />
              </div>
            </div>
          ) : (
            <img
              src={item.image?.replace("{size}", IMG_SIZES.medium)}
              alt={item.name || item.city}
              className={classes.hotelImage}
              onError={() => onErrorHandler("original")}
              loading="lazy"
            />
          )}
          {/* Low Availability Badge - shows when rooms are limited */}
          {item.remainingRooms < 5 && (
            <div className={classes.lowAvailabilityBadge}>
              {getStringRes(STR_KEY.LOW_AVAILABILITY).replace(
                STR_VARS.REMAINING_ROOMS,
                item.remainingRooms,
              )}
            </div>
          )}
        </div>

        {/* Hotel Details */}
        <div className={classes.hotelDetails}>
          <div className={classes.hotelHeader}>
            <h3 className={classes.hotelName}>{item.name || item.city}</h3>
            <div className={classes.starRating}>
              {Array.from({ length: 5 }, (_, i) => (
                <IconStarFilled
                  key={i}
                  size={14}
                  color={i < Math.round(rating) ? "#ffb60b" : "#e5e7eb"}
                  fill={i < Math.round(rating) ? "#ffb60b" : "transparent"}
                />
              ))}
              <span className={classes.ratingText}>({rating.toFixed(1)})</span>
            </div>
          </div>

          <p className={classes.location}>
            <IconMapPin size={14} />
            {item.location}
          </p>

          <div className={classes.detailsRow}>
            <span className={classes.detailItem}>
              <IconCalendar size={14} />
              {item.dates}
            </span>
            <span className={classes.detailItem}>
              <IconUsers size={14} />
              {guestCount}
            </span>
          </div>

          {/* Amenities - styled consistently with hotel results */}
          {item.amenities && item.amenities.length > 0 && (
            <div className={classes.amenities}>
              {item.amenities.slice(0, 3).map((amenity, idx) => (
                <div key={idx} className={classes.amenity}>
                  {getAmenityIcon(amenity)}
                  <span className={classes.amenityText}>
                    {getAmenityLabel(amenity, currentLanguage)}
                  </span>
                </div>
              ))}
            </div>
          )}

          {/* Pricing */}
          <div className={classes.pricingSection}>
            <div className={classes.priceContainer}>
              <div className={classes.pricePerPerson}>
                <span className={classes.priceAmount}>
                  {pricePerPerson ? `${pricePerPerson.toFixed(2)}` : "--"} {currencySymbol}
                </span>
                <span className={classes.priceLabel}>
                  {"/ " + getStringRes("cheap.hotels.price.per.person")}
                </span>
              </div>
            </div>
          </div>
        </div>
      </OverlayLink>

      {/* Favorite button - positioned outside the OverlayLink (can't nest a button in an <a>) */}
      <button
        type="button"
        className={classes.favoriteButton}
        aria-label={getStringRes("cheap.hotels.favorite")}
        onClick={onToggleFavorite}>
        {isFavorite ? (
          <IconHeartFilled size={16} color="#ff2965" />
        ) : (
          <IconHeart size={16} stroke={1.75} />
        )}
      </button>

      {/* Button - positioned outside the OverlayLink so it remains clickable */}
      <div className={classes.buttonContainer}>
        <Button
          name={getStringRes("cheap.hotels.view.deal")}
          primary
          cornerLg
          wide
          shadow
          onClick={handleViewDeal}
        />
      </div>
    </FocusContainer>
  );
}

function CheapHotelDealGrid({ items }) {
  const containerRef = useRef(null);
  useImageIntersectionObserver(containerRef);

  return (
    <motion.div
      ref={containerRef}
      initial="hidden"
      animate="visible"
      variants={listContainerVariants}
      className={classes.hotelsGrid}>
      {items.map((item) => (
        <motion.div
          whileHover={{
            scale: 1.01, // More subtle scale
            boxShadow: "0 8px 24px rgba(0, 0, 0, 0.12)",
            borderColor: "var(--primary-2-50)",
            transition: { duration: 0.2, ease: "easeOut" },
          }}
          whileTap={{ scale: 0.99 }}
          variants={listItemVariants}
          initial="hidden"
          animate="visible"
          tabIndex={-1}
          key={item.id}
          className={classes.hotelGridItem}
          style={{
            transform: "translateZ(0)", // Force GPU acceleration
            backfaceVisibility: "hidden",
          }}>
          <CheapHotelDeal item={item} />
        </motion.div>
      ))}
    </motion.div>
  );
}

const listContainerVariants = { hidden: { opacity: 0 }, visible: { opacity: 1 } };

const listItemVariants = {
  hidden: { opacity: 0, scale: 0.9, y: -10 },
  visible: {
    y: 0,
    opacity: 1,
    scale: 1,
    transition: { duration: 0.33, ease: "backOut" },
  },
};

export const STR_VARS = {
  company: "##COMPANY_NAME##",
  REMAINING_ROOMS: "##REMAINING_ROOMS##",
};

const STR_KEY = {
  LOW_AVAILABILITY: "cheap.hotels.low.availability", // "🔥 Only ##REMAINING_ROOMS## left!"
};

export function CheapHotelDeals({ data }) {
  const { isMobile } = useDeviceContext();
  const { getStringRes } = useLocaleContext();
  const [visibleCount, setVisibleCount] = useState(9);

  if (data?.length === 0) {
    return (
      <section
        className={`${classes.cheapHotelDeals} ${
          isMobile ? classes.mobile : classes.desktop
        }`}>
        <PaddedView>
          <div className={classes.heading}>
            <h2>
              {getStringRes("cheap.hotels.deals.title").replace(
                STR_VARS.company,
                window.SERVER_DATA.REACT_APP_PROJ_NAME,
              )}
            </h2>
          </div>
          <p className={classes.desc}>{getStringRes("cheap.hotels.deals.description")}</p>
          <div className={classes.emptyState}>
            <NoDataSVG />
            <p className={classes.emptyText}>{getStringRes("cheap.hotels.no.deals")}</p>
          </div>
        </PaddedView>
      </section>
    );
  }

  const visibleItems = data.slice(0, visibleCount);
  const hasMore = data.length > visibleCount;

  const handleViewMore = () => {
    setVisibleCount((prev) => prev + 9);
  };

  return (
    <section
      className={`${classes.cheapHotelDeals} ${isMobile ? classes.mobile : classes.desktop}`}>
      <PaddedView>
        <div className={classes.heading}>
          <h2>
            {getStringRes("cheap.hotels.deals.title").replace(
              STR_VARS.company,
              window.SERVER_DATA.REACT_APP_PROJ_NAME,
            )}
          </h2>
        </div>
        <p className={classes.desc}>{getStringRes("cheap.hotels.deals.description")}</p>
        <CheapHotelDealGrid items={visibleItems} />
        {hasMore && (
          <div className={classes.viewMoreContainer}>
            <button className={classes.viewMoreButton} onClick={handleViewMore}>
              {getStringRes("cheap.hotels.view.more")}
            </button>
          </div>
        )}
      </PaddedView>
    </section>
  );
}
